<template>
  <v-container>
    <!-- Toolbar -->
    <div class="d-flex align-center mb-3">
      <v-btn
        v-if="currentPath !== ROOT_PATH"
        color="primary"
        outlined
        class="mr-2"
        @click="goUp"
      >
        <v-icon left>{{ icons.mdiArrowLeft }}</v-icon>
        Back
      </v-btn>
      <v-btn
        color="primary"
        outlined
        class="mr-2"
        :loading="loading"
        @click="refresh"
      >
        <v-icon left>{{ icons.mdiRefresh }}</v-icon>
        Refresh
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="openReportDialog">
        <v-icon left>{{ icons.mdiFileChartOutline }}</v-icon>
        Record Count Report
      </v-btn>
    </div>

    <!-- Record Count Report date-range dialog -->
    <v-dialog v-model="showReportDialog" persistent max-width="420px">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium py-2 px-4">
          Record Count Report
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4 pb-0">
          <v-row dense>
            <v-col cols="6">
              <ClarionDateControl
                v-model="reportFromDate"
                label="From Date"
                :isIsoDate="true"
                :isToggle="false"
              />
            </v-col>
            <v-col cols="6">
              <ClarionDateControl
                v-model="reportToDate"
                label="To Date"
                :isIsoDate="true"
                :isToggle="false"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-4 pb-3">
          <v-spacer></v-spacer>
          <v-btn text color="blue darken-1" @click="showReportDialog = false">CLOSE</v-btn>
          <v-btn
            color="blue darken-1"
            :loading="generatingReport"
            :disabled="!reportFromDate || !reportToDate"
            @click="openRecordCountReport"
          >
            GENERATE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Breadcrumb nav -->
    <v-breadcrumbs :items="breadcrumbs" divider="/" class="px-0 pt-2 pb-1">
      <template v-slot:[`item`]="{ item }">
        <v-breadcrumbs-item
          :disabled="item.disabled"
          style="cursor: pointer;"
          @click="!item.disabled && navigateTo(item.path)"
        >
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <!-- Search -->
    <v-text-field
      v-model="search"
      :prepend-inner-icon="icons.mdiMagnify"
      label="Filter"
      clearable
      dense
      outlined
      style="max-width: 380px;"
      class="mb-2"
    />

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

    <v-alert v-if="!loading && error" type="error" outlined>{{ error }}</v-alert>

    <v-alert v-if="!loading && !error && filteredEntries.length === 0" type="info" outlined>
      {{ search ? 'No entries match filter.' : 'Directory is empty.' }}
    </v-alert>

    <v-data-table
      v-if="!error && filteredEntries.length > 0"
      :headers="headers"
      :items="filteredEntries"
      :items-per-page="50"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
      :sort-by="['mtime']"
      :sort-desc="[true]"
      class="elevation-1"
      @click:row="onRowClick"
    >
      <template v-slot:[`item.name`]="{ item }">
        <span style="cursor: pointer;">
          <v-icon small :color="item.type === 'dir' ? 'amber darken-2' : iconColor(item.ext)">
            {{ item.type === 'dir' ? icons.mdiFolder : fileIcon(item.ext) }}
          </v-icon>
          {{ item.name }}
        </span>
      </template>

      <template v-slot:[`item.sizeFormatted`]="{ item }">
        {{ item.type === 'dir' ? '—' : item.sizeFormatted }}
      </template>

      <template v-slot:[`item.mtime`]="{ item }">
        {{ formatDate(item.mtime) }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn
          v-if="item.type === 'file'"
          color="primary"
          small
          :loading="downloading === item.path"
          @click.stop="download(item)"
        >
          <v-icon left small>{{ icons.mdiDownload }}</v-icon>
          Download
        </v-btn>
        <span v-if="item.type === 'dir'">
          <v-btn
            color="secondary"
            small
            class="mr-1"
            @click.stop="navigateTo(item.path)"
          >
            <v-icon left small>{{ icons.mdiFolderOpenOutline }}</v-icon>
            Open
          </v-btn>
          <v-btn
            color="teal"
            dark
            small
            :loading="zipping === item.path"
            @click.stop="downloadZip(item)"
          >
            <v-icon left small>{{ icons.mdiZipBox }}</v-icon>
            ZIP
          </v-btn>
        </span>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import {
  mdiArrowLeft,
  mdiFolder,
  mdiFolderOpenOutline,
  mdiDownload,
  mdiZipBox,
  mdiMagnify,
  mdiFilePdfBox,
  mdiFileDelimited,
  mdiCodeJson,
  mdiFileDocumentOutline,
  mdiFileImage,
  mdiFileOutline,
  mdiFileChartOutline,
  mdiRefresh,
} from '@mdi/js';
import { clients } from '../../util/clients';
import ClarionDateControl from '../common/ClarionDateControl.vue';

const { backendRest } = clients.direct;

const ROOT_PATH = 'exports/f/ascii/over-night-reports';

const FILE_ICONS = {
  pdf: { icon: mdiFilePdfBox, color: 'red darken-1' },
  csv: { icon: mdiFileDelimited, color: 'green darken-1' },
  json: { icon: mdiCodeJson, color: 'orange darken-1' },
  txt: { icon: mdiFileDocumentOutline, color: 'grey' },
  png: { icon: mdiFileImage, color: 'blue darken-1' },
  jpg: { icon: mdiFileImage, color: 'blue darken-1' },
  jpeg: { icon: mdiFileImage, color: 'blue darken-1' },
};

export default {
  name: 'OvernightReportsBrowser',
  components: { ClarionDateControl },
  data: () => ({
    ROOT_PATH,
    icons: {
      mdiArrowLeft,
      mdiFolder,
      mdiFolderOpenOutline,
      mdiDownload,
      mdiZipBox,
      mdiMagnify,
      mdiFileOutline,
      mdiFileChartOutline,
      mdiRefresh,
    },
    entries: [],
    currentPath: ROOT_PATH,
    loading: false,
    downloading: null,
    zipping: null,
    error: null,
    search: '',
    showReportDialog: false,
    reportFromDate: '',
    reportToDate: '',
    generatingReport: false,
    headers: [
      {
        text: 'Name', value: 'name', sortable: true,
      },
      {
        text: 'Type', value: 'ext', sortable: true, width: '80px',
      },
      {
        text: 'Size', value: 'sizeFormatted', sortable: false, width: '110px',
      },
      {
        text: 'Modified', value: 'mtime', sortable: true, width: '180px',
      },
      {
        text: 'Actions', value: 'actions', sortable: false, width: '200px',
      },
    ],
  }),
  computed: {
    breadcrumbs() {
      const subPath = this.currentPath === ROOT_PATH
        ? ''
        : this.currentPath.slice(ROOT_PATH.length + 1);

      const crumbs = [{
        text: 'over-night-reports',
        path: ROOT_PATH,
        disabled: subPath === '',
      }];

      if (subPath) {
        const parts = subPath.split('/');
        parts.forEach((part, i) => {
          const p = `${ROOT_PATH}/${parts.slice(0, i + 1).join('/')}`;
          crumbs.push({ text: part, path: p, disabled: i === parts.length - 1 });
        });
      }
      return crumbs;
    },
    filteredEntries() {
      if (!this.search) return this.entries;
      const q = this.search.toLowerCase();
      return this.entries.filter((e) => e.name.toLowerCase().includes(q));
    },
  },
  created() {
    this.load(ROOT_PATH);
  },
  methods: {
    async load(relPath) {
      this.loading = true;
      this.error = null;
      try {
        await this.$store.dispatch('reAuth');
        const resp = await backendRest.get('/data-browser/list', { params: { path: relPath } });
        this.entries = resp.data.entries;
        this.currentPath = relPath;
        this.search = '';
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || 'Failed to load directory.';
      } finally {
        this.loading = false;
      }
    },
    navigateTo(path) {
      this.load(path);
    },
    // Reload the current directory, preserving the active filter.
    refresh() {
      const currentSearch = this.search;
      this.load(this.currentPath).then(() => { this.search = currentSearch; });
    },
    goUp() {
      const parent = this.currentPath.split('/').slice(0, -1).join('/') || ROOT_PATH;
      const target = parent.startsWith(ROOT_PATH) ? parent : ROOT_PATH;
      this.load(target);
    },
    onRowClick(item) {
      if (item.type === 'dir') this.load(item.path);
    },
    async download(item) {
      this.downloading = item.path;
      try {
        await this.$store.dispatch('reAuth');
        const resp = await backendRest.get('/data-browser/download', {
          params: { path: item.path },
          responseType: 'blob',
        });
        const disposition = resp.headers['content-disposition'] || '';
        const match = disposition.match(/filename="(.+?)"/);
        const filename = match ? match[1] : item.name;
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (e) {
        this.$store.dispatch('flashError', 'Download failed.', { root: true });
      } finally {
        this.downloading = null;
      }
    },
    async downloadZip(item) {
      this.zipping = item.path;
      try {
        await this.$store.dispatch('reAuth');
        const resp = await backendRest.get('/data-browser/download-zip', {
          params: { path: item.path },
          responseType: 'blob',
          timeout: 120000,
        });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${item.name}.zip`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (e) {
        this.$store.dispatch('flashError', 'ZIP download failed.', { root: true });
      } finally {
        this.zipping = null;
      }
    },
    todayIso() {
      const d = new Date();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    },
    // Derive a YYYY-MM-DD from the current folder name; supports the two folder
    // schemes (YYYY-MM-DD and legacy MMDDYYYY). Falls back to today.
    folderDateIso() {
      const seg = (this.currentPath || '').split('/').pop() || '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(seg)) return seg;
      const m = seg.match(/^(\d{2})(\d{2})(\d{4})$/);
      if (m) return `${m[3]}-${m[1]}-${m[2]}`;
      return this.todayIso();
    },
    openReportDialog() {
      const iso = this.folderDateIso();
      this.reportFromDate = iso;
      this.reportToDate = iso;
      this.showReportDialog = true;
    },
    async openRecordCountReport() {
      this.generatingReport = true;
      try {
        await this.$store.dispatch('reAuth');
        const resp = await backendRest.get('/tax/export-record-count-report', {
          params: { fromDate: this.reportFromDate, toDate: this.reportToDate },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        this.showReportDialog = false;
      } catch (e) {
        this.$store.dispatch('flashError', 'Failed to generate Record Count Report.', { root: true });
      } finally {
        this.generatingReport = false;
      }
    },
    fileIcon(ext) {
      return (FILE_ICONS[ext] || {}).icon || mdiFileOutline;
    },
    iconColor(ext) {
      return (FILE_ICONS[ext] || {}).color || 'grey darken-1';
    },
    formatDate(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString();
    },
  },
};
</script>
